import { type FormEvent, type ReactNode, useState } from 'react';
import { Button, Input, cn } from '@wundo/ui';
import { ApiError } from '@wundo/client/api.ts';
import {
  requestSignInLink,
  startGoogleSignIn,
  startMicrosoftSignIn,
} from '@wundo/client/auth.ts';
import { GoogleIcon, MicrosoftIcon } from '@/components/ProviderIcons.tsx';
import { rememberRequestedRedirect } from '@/lib/requestedSignInRedirect.ts';
import { m } from '@/paraglide/messages.js';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SignInChoicesProps {
  /** Where sign-in returns to, as the login screen was asked for it. */
  redirect: string | undefined;
  /** Why a sign-in elsewhere sent the person back here, or empty. */
  refusal: string;
  /** Kept by the screen, so "another address" comes back to the one typed. */
  email: string;
  onEmailChange: (email: string) => void;
  /** The backend accepted the address, so a link is on its way. */
  onSent: () => void;
}

/** The ways in: Google, Microsoft, or a link by email. */
export function SignInChoices({
  redirect,
  refusal,
  email,
  onEmailChange,
  onSent,
}: SignInChoicesProps) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!EMAIL_PATTERN.test(email)) {
      setError(m.login_email_invalid());
      return;
    }

    setError('');
    setLoading(true);
    try {
      await requestSignInLink(email, redirect);
      rememberRequestedRedirect(redirect);
      onSent();
    } catch (caught) {
      setError(signInErrorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  /* On success the browser navigates away, so loading is only cleared on failure. */
  async function handleSocial(start: () => Promise<string>) {
    setError('');
    setLoading(true);
    try {
      window.location.href = await start();
    } catch {
      setError(m.login_error_generic());
      setLoading(false);
    }
  }

  return (
    <>
      <div>
        <h1 className='mb-1.5 text-xl font-semibold tracking-tight'>{m.login_title()}</h1>
        <p className='text-md text-ink-muted'>{m.login_subtitle()}</p>
      </div>

      {/* Its own slot: this failure happened elsewhere, unlike the form errors below. */}
      {refusal && (
        <p
          role='alert'
          className='mt-6.5 rounded-lg border border-danger-line bg-danger-fill px-4 py-3 text-sm font-medium text-danger'
        >
          {refusal}
        </p>
      )}

      <div className={cn('flex flex-col gap-2.5', refusal ? 'mt-4' : 'mt-6.5')}>
        <ProviderButton
          disabled={loading}
          onClick={() => void handleSocial(() => startGoogleSignIn(redirect))}
        >
          <GoogleIcon />
          {m.login_google()}
        </ProviderButton>
        <ProviderButton
          disabled={loading}
          onClick={() => void handleSocial(() => startMicrosoftSignIn(redirect))}
        >
          <MicrosoftIcon />
          {m.login_microsoft()}
        </ProviderButton>
      </div>

      <div className='my-5.5 flex items-center gap-3.5 text-xs font-semibold tracking-wide text-ink-muted uppercase'>
        <span className='h-px flex-1 bg-line' />
        {m.login_divider()}
        <span className='h-px flex-1 bg-line' />
      </div>

      <form
        className='flex flex-col gap-2.75'
        onSubmit={(event) => void handleSubmit(event)}
        noValidate
      >
        <div>
          <label
            className='mb-1.75 block text-sm font-semibold text-ink-secondary'
            htmlFor='email'
          >
            {m.login_email_label()}
          </label>
          <Input
            id='email'
            type='email'
            fieldSize='lg'
            placeholder={m.login_email_placeholder()}
            value={email}
            invalid={Boolean(error)}
            onChange={(event) => {
              onEmailChange(event.target.value);
              if (error) {
                setError('');
              }
            }}
          />
        </div>
        {error && <span className='text-sm font-medium text-danger'>{error}</span>}
        <Button
          type='submit'
          variant='accent'
          shape='block'
          size='lg'
          fullWidth
          loading={loading}
        >
          {m.login_submit()}
        </Button>
      </form>

      <div className='mt-7.5 flex justify-center gap-4.5 text-xs text-ink-muted'>
        <a className='hover:text-ink-muted' href='https://wundo.ai/privacy-policy'>
          {m.login_privacy()}
        </a>
        <a className='hover:text-ink-muted' href='https://wundo.ai/trust-center/'>
          {m.login_trust_center()}
        </a>
      </div>
    </>
  );
}

interface ProviderButtonProps {
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}

function ProviderButton({ disabled, onClick, children }: ProviderButtonProps) {
  return (
    <Button
      type='button'
      variant='quiet'
      shape='block'
      size='lg'
      fullWidth
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </Button>
  );
}

/*
 * Both refusals name what the person can do. SSO_REQUIRED: the organisation
 * turned links off, so the message points at the provider buttons above.
 * UNKNOWN_ACCOUNT: everyone is provisioned up front, so a typo or a personal
 * mailbox; better said than waiting for mail that was never sent.
 */
function signInErrorMessage(caught: unknown): string {
  if (!(caught instanceof ApiError)) {
    return m.login_error_generic();
  }
  if (caught.code === 'SSO_REQUIRED') {
    return m.login_sso_required();
  }
  if (caught.code === 'UNKNOWN_ACCOUNT') {
    return m.login_unknown_account();
  }
  if (caught.type === 'rate_limited') {
    return m.login_error_rate_limited();
  }
  return m.login_error_generic();
}
